import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ArrowRight, X, Menu, ExternalLink } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

/* ── Design tokens ──────────────────────────────────────────────── */
const C = {
  bg:        '#ffffff',
  bgScroll:  '#ffffff',
  topBar:    '#0f172a',
  surface:   '#ffffff',
  border:    '#e8ecf0',
  text:      '#0f172a',
  muted:     '#64748b',
  orange:    '#E8611A',
};

/* ── Menu data ───────────────────────────────────────────────────── */
const menuItems = [
  {
    name: 'Accueil',
    path: '/',
  },
  {
    name: 'Qui sommes-nous',
    path: '#',
    submenu: [
      { name: 'À propos de nous', path: '/a-propos' },
      { name: 'Notre équipe',     path: '/notre-equipe' },
      { name: 'Nous rejoindre',   path: '/rejoignez-nous' },
    ],
  },
  {
    name: 'Produits & Services',
    path: '/services',
    submenu: [
      { name: 'NigerEmploi.com',    externalLink: 'https://nigeremploi.com',    badge: 'Emploi',  color: '#16a34a' },
      { name: 'SesameHosting.com',  externalLink: 'https://sesamehosting.com',  badge: 'Hosting', color: '#E8611A' },
    ],
  },
  {
    name: 'Activités',
    path: '/activities',
    submenu: [
      { name: 'Études & Conseils informatiques', path: '/etude-conseil' },
      { name: 'Formation',                       path: '/formations' },
      { name: 'Développement de Solutions Web & Mobile', path: '/web-media' },
    ],
  },
  {
    name: 'Références',
    path: '/references',
  },
];

/* ── Submenu item ───────────────────────────────────────────────── */
function SubItem({ item, onClose }) {
  const base = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 14px',
    borderRadius: 8,
    fontSize: '0.845rem',
    fontWeight: 500,
    color: '#475569',
    textDecoration: 'none',
    transition: 'background .15s, color .15s',
    cursor: 'pointer',
    letterSpacing: '0.01em',
  };

  const hoverIn = e => {
    e.currentTarget.style.background = '#fdf5f1';
    e.currentTarget.style.color = '#E8611A';
  };
  const hoverOut = e => {
    e.currentTarget.style.background = 'transparent';
    e.currentTarget.style.color = '#475569';
  };

  if (item.externalLink) {
    return (
      <a
        href={item.externalLink}
        target="_blank"
        rel="noopener noreferrer"
        style={base}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
        onClick={onClose}
      >
        {item.badge && (
          <span style={{
            fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
            padding: '2px 6px', borderRadius: 4,
            background: `${item.color}20`, color: item.color, border: `1px solid ${item.color}35`,
            flexShrink: 0,
          }}>
            {item.badge}
          </span>
        )}
        <span style={{ flex: 1 }}>{item.name}</span>
        <ExternalLink size={11} style={{ opacity: 0.35, flexShrink: 0 }} />
      </a>
    );
  }

  return (
    <Link
      to={item.path}
      style={base}
      onMouseEnter={hoverIn}
      onMouseLeave={hoverOut}
      onClick={onClose}
    >
      <span style={{ flex: 1 }}>{item.name}</span>
    </Link>
  );
}

/* ── Dropdown panel ─────────────────────────────────────────────── */
function Dropdown({ item, visible, onClose, onMouseEnter, onMouseLeave }) {
  return (
    <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} style={{
      position: 'absolute',
      top: 'calc(100% + 10px)',
      left: '50%',
      minWidth: 230,
      background: '#ffffff',
      borderRadius: 14,
      boxShadow: '0 16px 48px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)',
      border: '1px solid #e8ecf0',
      padding: '6px',
      zIndex: 200,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(-6px)',
      pointerEvents: visible ? 'all' : 'none',
      transition: 'opacity .18s ease, transform .18s ease',
    }}>
      <div style={{
        position: 'absolute', top: -5, left: '50%', transform: 'translateX(-50%) rotate(45deg)',
        width: 10, height: 10,
        background: '#ffffff',
        border: '1px solid #e8ecf0',
        borderRight: 'none', borderBottom: 'none',
      }} />
      <div style={{ position: 'absolute', top: 0, left: 10, right: 10, height: 2, borderRadius: '0 0 2px 2px', background: 'linear-gradient(90deg, #E8611A, #f5a050)' }} />
      <div style={{ paddingTop: 4 }}>
        {item.submenu.map((sub) => (
          <SubItem key={sub.name} item={sub} onClose={onClose} />
        ))}
      </div>
    </div>
  );
}

/* ── Main Header ─────────────────────────────────────────────────── */
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();
  const headerRef = useRef(null);
  const closeTimer = useRef(null);

  const openDropdown = (name) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(name);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [location]);

  const isActive = (item) =>
    item.path && item.path !== '#' && location.pathname === item.path;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap');

        .ayd-nav-link {
          position: relative;
          display: flex; align-items: center; gap: 5px;
          padding: 7px 13px;
          border-radius: 7px;
          font-size: 0.855rem; font-weight: 600;
          color: #334155;
          cursor: pointer;
          transition: color .18s, background .18s;
          white-space: nowrap;
          border: none; background: transparent;
          font-family: 'DM Sans', sans-serif;
          text-decoration: none;
          letter-spacing: 0.01em;
        }
        .ayd-nav-link:hover { color: #E8611A; background: #fdf5f1; }
        .ayd-nav-link.open   { color: #E8611A; background: #fdf5f1; }
        .ayd-nav-link.active { color: #E8611A; }
        .ayd-nav-link.active::after {
          content: '';
          position: absolute; bottom: -1px; left: 50%;
          transform: translateX(-50%);
          width: 16px; height: 2px;
          background: #E8611A; border-radius: 2px;
        }

        @media (max-width: 900px)  { .ayd-desktop { display: none !important; } .ayd-burger { display: flex !important; } }
        @media (min-width: 901px)  { .ayd-mobile  { display: none !important; } .ayd-burger { display: none !important; } }
      `}</style>

      <header ref={headerRef} style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9000,
        fontFamily: "'DM Sans', sans-serif",
        background: '#ffffff',
        borderBottom: '1px solid #e8ecf0',
        boxShadow: scrolled ? '0 4px 24px rgba(0,0,0,0.08)' : 'none',
        transition: 'box-shadow .3s',
      }}>

        {/* ── Top micro-bar ── */}
        <div style={{ background: '#0f172a', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', height: 34, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>

            {/* Right: platforms */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {[
                { label: 'NigerEmploi',    href: 'https://nigeremploi.com',    color: '#4ade80', dot: '#16a34a' },
                { label: 'SesameHosting',  href: 'https://sesamehosting.com/', color: '#fb923c', dot: '#E8611A' },
              ].map((p, i) => (
                <React.Fragment key={p.label}>
                  {i > 0 && <span style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.08)', margin: '0 4px' }} />}
                  <a href={p.href} target="_blank" rel="noopener noreferrer" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '3px 10px', borderRadius: 4,
                    fontSize: '0.67rem', fontWeight: 600, color: p.color,
                    textDecoration: 'none', letterSpacing: '0.04em',
                    background: 'transparent',
                    transition: 'background .15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: p.dot, flexShrink: 0 }} />
                    {p.label}
                  </a>
                </React.Fragment>
              ))}
            </div>

          </div>
        </div>

        {/* ── Main nav bar ── */}
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', height: 62 }}>

          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0, marginRight: 36 }}>
            <img
              src="/images/logo-aydees.png"
              alt="Aydees"
              style={{ height: 38, width: 'auto', objectFit: 'contain' }}
            />
          </Link>

          {/* Desktop nav */}
          <div className="ayd-desktop" style={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            {menuItems.map((item) => (
              <div
                key={item.name}
                style={{ position: 'relative' }}
                onMouseEnter={() => item.submenu && openDropdown(item.name)}
                onMouseLeave={() => item.submenu && scheduleClose()}
              >
                {item.submenu ? (
                  <button className={`ayd-nav-link${openMenu === item.name ? ' open' : ''}`}>
                    {item.name}
                    <ChevronDown size={12} style={{
                      transition: 'transform .2s',
                      transform: openMenu === item.name ? 'rotate(180deg)' : 'none',
                      opacity: 0.5,
                    }} />
                  </button>
                ) : (
                  <Link to={item.path} className={`ayd-nav-link${isActive(item) ? ' active' : ''}`}>
                    {item.name}
                  </Link>
                )}

                {item.submenu && (
                  <Dropdown
                    item={item}
                    visible={openMenu === item.name}
                    onClose={() => setOpenMenu(null)}
                    onMouseEnter={() => openDropdown(item.name)}
                    onMouseLeave={() => scheduleClose()}
                  />
                )}
              </div>
            ))}
          </div>

          {/* CTA desktop */}
          <div className="ayd-desktop" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Link
              to="/rejoignez-nous"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '8px 18px', borderRadius: 8,
                background: 'linear-gradient(135deg, #E8611A 0%, #c94e0e 100%)',
                color: '#fff', fontWeight: 600, fontSize: '0.82rem',
                textDecoration: 'none', letterSpacing: '0.02em',
                boxShadow: '0 0 0 1px rgba(232,97,26,0.4), 0 4px 16px rgba(232,97,26,0.25)',
                transition: 'box-shadow .2s, transform .2s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = '0 0 0 1px rgba(232,97,26,0.6), 0 6px 22px rgba(232,97,26,0.38)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = '0 0 0 1px rgba(232,97,26,0.4), 0 4px 16px rgba(232,97,26,0.25)';
                e.currentTarget.style.transform = 'none';
              }}
            >
              Nous contacter <ArrowRight size={13} />
            </Link>
          </div>

          {/* Mobile burger */}
          <button
            className="ayd-burger"
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{
              display: 'none', marginLeft: 'auto',
              alignItems: 'center', justifyContent: 'center',
              width: 38, height: 38, borderRadius: 8,
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
              cursor: 'pointer', color: '#334155',
              transition: 'border-color .2s, background .2s',
            }}
          >
            {mobileOpen ? <X size={17} /> : <Menu size={17} />}
          </button>
        </div>

        {/* ── Mobile menu ── */}
        <div
          className="ayd-mobile"
          style={{
            display: 'none',
            background: '#ffffff',
            borderTop: '1px solid #e8ecf0',
            maxHeight: mobileOpen ? 640 : 0,
            overflow: 'hidden',
            transition: 'max-height .35s cubic-bezier(.4,0,.2,1)',
          }}
        >
          <div style={{ padding: '10px 16px 22px' }}>
            {menuItems.map((item) => (
              <div key={item.name}>
                {item.submenu ? (
                  <>
                    <button
                      onClick={() => setMobileExpanded(mobileExpanded === item.name ? null : item.name)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '11px 14px', borderRadius: 9, background: 'transparent', border: 'none',
                        fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.88rem', color: '#334155',
                        cursor: 'pointer', transition: 'background .18s, color .18s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#fdf5f1'; e.currentTarget.style.color = '#E8611A'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#334155'; }}
                    >
                      {item.name}
                      <ChevronDown size={14} style={{ transition: 'transform .25s', transform: mobileExpanded === item.name ? 'rotate(180deg)' : 'none', color: '#94a3b8' }} />
                    </button>
                    <div style={{
                      overflow: 'hidden',
                      maxHeight: mobileExpanded === item.name ? 400 : 0,
                      transition: 'max-height .3s ease',
                      paddingLeft: 10,
                    }}>
                      {item.submenu.map((sub) => (
                        <SubItem key={sub.name} item={sub} onClose={() => setMobileOpen(false)} />
                      ))}
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    style={{
                      display: 'block', padding: '11px 14px', borderRadius: 9,
                      fontWeight: 600, fontSize: '0.88rem',
                      color: isActive(item) ? '#E8611A' : '#334155',
                      textDecoration: 'none',
                      background: isActive(item) ? '#fdf5f1' : 'transparent',
                      transition: 'background .18s, color .18s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fdf5f1'; e.currentTarget.style.color = '#E8611A'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isActive(item) ? '#fdf5f1' : 'transparent'; e.currentTarget.style.color = isActive(item) ? '#E8611A' : '#334155'; }}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}

            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0efec', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {[
                { name: 'NigerEmploi',   href: 'https://nigeremploi.com',    color: '#16a34a' },
                { name: 'SésameHosting', href: 'https://sesamehosting.com/', color: '#E8611A' },
              ].map((p) => (
                <a key={p.name} href={p.href} target="_blank" rel="noopener noreferrer" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '5px 11px', borderRadius: 6,
                  background: `${p.color}12`, border: `1px solid ${p.color}28`,
                  fontSize: '0.72rem', fontWeight: 600, color: p.color, textDecoration: 'none',
                }}>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: p.color }} />
                  {p.name}
                </a>
              ))}
            </div>

            <Link to="/rejoignez-nous" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              marginTop: 14, padding: '12px', borderRadius: 9,
              background: 'linear-gradient(135deg, #E8611A, #c04b10)',
              color: '#fff', fontWeight: 700, fontSize: '0.86rem', textDecoration: 'none',
              boxShadow: '0 4px 16px rgba(232,97,26,0.3)',
            }}>
              Nous contacter <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* Bottom accent line */}
        <div style={{ height: 2, background: 'linear-gradient(90deg, #E8611A 0%, #f5a050 50%, #16a34a 100%)' }} />
      </header>
    </>
  );
}